import { useState } from 'react';
import { motion } from 'framer-motion';
import { MapPin, Phone, Clock, Mail, ExternalLink, Navigation, Star } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';

const contactInfo = [
  {
    icon: MapPin,
    title: 'Adresse',
    content: 'Av. Ritz 33, 1950 Sion, Suisse',
    action: 'Obtenir l\'itinéraire',
    href: 'https://www.google.com/maps/dir/?api=1&destination=Av.+Ritz+33,+1950+Sion,+Suisse',
  },
  {
    icon: Phone,
    title: 'Téléphone',
    content: '+41 27 323 23 16',
    action: 'Appeler maintenant',
    href: 'tel:+41273232316',
  },
  {
    icon: Mail,
    title: 'Email',
    content: 'info@alma-fleurs-sion.ch',
    action: 'Envoyer un email',
    href: 'mailto:info@alma-fleurs-sion.ch',
  },
];

const hours = [
  { day: 'Lundi', time: '09:00 - 18:30', open: true },
  { day: 'Mardi', time: '09:00 - 18:30', open: true },
  { day: 'Mercredi', time: '09:00 - 18:30', open: true },
  { day: 'Jeudi', time: '09:00 - 18:30', open: true },
  { day: 'Vendredi', time: '09:00 - 18:30', open: true },
  { day: 'Samedi', time: '09:00 - 17:00', open: true },
  { day: 'Dimanche', time: 'Fermé', open: false },
];

export default function ContactPage() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    // Simulate form submission
    setTimeout(() => {
      toast.success('Message envoyé avec succès !', {
        description: 'Nous vous répondrons dans les plus brefs délais.',
      });
      setFormData({ name: '', email: '', phone: '', subject: '', message: '' });
      setIsSubmitting(false);
    }, 1500);
  };

  return (
    <div data-testid="contact-page" className="pt-24">
      {/* Hero Section */}
      <section className="py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center max-w-3xl mx-auto">
            <motion.span
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="inline-block text-accent font-accent italic text-lg mb-4"
            >
              Nous sommes à votre écoute
            </motion.span>
            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="font-serif text-4xl md:text-5xl lg:text-6xl font-semibold text-primary tracking-tight"
            >
              Contactez-nous
            </motion.h1>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="mt-6 text-lg text-muted-foreground"
            >
              Une question, un projet floral ou une commande spéciale ? N'hésitez pas à nous contacter.
              Nous serons ravis de vous accompagner.
            </motion.p>
          </div>
        </div>
      </section>

      {/* Contact Cards */}
      <section className="pb-16">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid md:grid-cols-3 gap-6">
            {contactInfo.map((info, index) => (
              <motion.a
                key={info.title}
                href={info.href}
                target={info.href.startsWith('http') ? '_blank' : undefined}
                rel={info.href.startsWith('http') ? 'noopener noreferrer' : undefined}
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 * index }}
                data-testid={`contact-card-${info.title.toLowerCase()}`}
                className="group bg-white p-8 rounded-3xl border border-primary/5 hover:shadow-xl hover:-translate-y-1 transition-all text-center"
              >
                <div className="w-16 h-16 rounded-2xl bg-secondary mx-auto flex items-center justify-center mb-6 group-hover:bg-accent/20 transition-colors">
                  <info.icon className="w-7 h-7 text-primary" />
                </div>
                <h3 className="font-serif text-xl font-semibold text-primary mb-2">{info.title}</h3>
                <p className="text-muted-foreground mb-4">{info.content}</p>
                <span className="inline-flex items-center gap-2 text-accent font-medium">
                  {info.action}
                  <ExternalLink className="w-4 h-4" />
                </span>
              </motion.a>
            ))}
          </div>
        </div>
      </section>

      {/* Main Contact Section */}
      <section className="py-16 md:py-24 bg-secondary/30">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-20">
            {/* Form */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="font-serif text-3xl md:text-4xl font-semibold text-primary mb-8">
                Envoyez-nous un message
              </h2>
              <form onSubmit={handleSubmit} data-testid="contact-form" className="space-y-6">
                <div className="grid sm:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-primary mb-2">
                      Nom complet *
                    </label>
                    <Input
                      placeholder="Votre nom"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      required
                      data-testid="input-name"
                      className="rounded-xl border-primary/10 focus:border-accent h-12"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-primary mb-2">
                      Email *
                    </label>
                    <Input
                      type="email"
                      placeholder="votre@email.ch"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      required
                      data-testid="input-email"
                      className="rounded-xl border-primary/10 focus:border-accent h-12"
                    />
                  </div>
                </div>
                <div className="grid sm:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-primary mb-2">
                      Téléphone
                    </label>
                    <Input
                      type="tel"
                      placeholder="+41 XX XXX XX XX"
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      data-testid="input-phone"
                      className="rounded-xl border-primary/10 focus:border-accent h-12"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-primary mb-2">
                      Sujet
                    </label>
                    <Input
                      placeholder="Objet de votre message"
                      value={formData.subject}
                      onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                      data-testid="input-subject"
                      className="rounded-xl border-primary/10 focus:border-accent h-12"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-primary mb-2">
                    Message *
                  </label>
                  <Textarea
                    placeholder="Décrivez votre demande ou projet..."
                    value={formData.message}
                    onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                    required
                    rows={6}
                    data-testid="input-message"
                    className="rounded-xl border-primary/10 focus:border-accent resize-none"
                  />
                </div>
                <Button
                  type="submit"
                  disabled={isSubmitting}
                  data-testid="submit-btn"
                  className="w-full rounded-full py-6 text-lg bg-primary hover:bg-primary/90 transition-all"
                >
                  {isSubmitting ? 'Envoi en cours...' : 'Envoyer le message'}
                </Button>
              </form>
            </motion.div>

            {/* Hours & Info */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="space-y-8"
            >
              {/* Hours */}
              <div className="bg-white p-8 rounded-3xl border border-primary/5">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-12 h-12 rounded-2xl bg-secondary flex items-center justify-center">
                    <Clock className="w-6 h-6 text-primary" />
                  </div>
                  <h3 className="font-serif text-2xl font-semibold text-primary">
                    Horaires d'ouverture
                  </h3>
                </div>
                <div className="space-y-3">
                  {hours.map((item) => (
                    <div
                      key={item.day}
                      className={`flex justify-between py-3 px-4 rounded-xl ${
                        !item.open ? 'bg-red-50' : 'bg-secondary/50'
                      }`}
                    >
                      <span className={`font-medium ${!item.open ? 'text-red-600' : 'text-primary'}`}>
                        {item.day}
                      </span>
                      <span className={!item.open ? 'text-red-600' : 'text-muted-foreground'}>
                        {item.time}
                      </span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Google Rating */}
              <div className="bg-white p-8 rounded-3xl border border-primary/5">
                <div className="flex items-center gap-3 mb-4">
                  <div className="flex gap-1">
                    {[...Array(5)].map((_, i) => (
                      <Star key={i} className="w-5 h-5 text-accent fill-accent" />
                    ))}
                  </div>
                  <span className="font-serif text-2xl font-semibold text-primary">4.9/5</span>
                </div>
                <p className="text-muted-foreground mb-4">
                  Basé sur 193 avis Google de nos clients satisfaits.
                </p>
                <a
                  href="https://www.google.com/maps/place/Alma+Fleur+SA"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="google-reviews-link"
                  className="inline-flex items-center gap-2 text-accent font-medium hover:underline"
                >
                  Voir tous les avis
                  <ExternalLink className="w-4 h-4" />
                </a>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Map Section */}
      <section className="py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="font-serif text-3xl md:text-4xl font-semibold text-primary mb-4">
              Venez nous rendre visite
            </h2>
            <p className="text-muted-foreground">
              Notre boutique vous accueille au cœur de Sion, à deux pas du centre-ville.
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="relative"
          >
            <div className="rounded-3xl overflow-hidden shadow-2xl border border-primary/10 h-[500px]">
              <iframe
                data-testid="google-map-iframe"
                title="Alma Fleur SA - Localisation"
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2769.4!2d7.358!3d46.233!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2sAv.%20Ritz%2033%2C%201950%20Sion!5e0!3m2!1sfr!2sch!4v1234567890"
                width="100%"
                height="100%"
                style={{ border: 0 }}
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              />
            </div>
            
            {/* Floating Card */}
            <div className="absolute bottom-6 left-6 right-6 md:left-6 md:right-auto md:max-w-sm glass rounded-2xl p-6 shadow-xl">
              <h3 className="font-serif text-xl font-semibold text-primary mb-2">Alma Fleur SA</h3>
              <p className="text-muted-foreground text-sm mb-4">
                Av. Ritz 33, 1950 Sion, Suisse
              </p>
              <a
                href="https://www.google.com/maps/dir/?api=1&destination=Av.+Ritz+33,+1950+Sion,+Suisse"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="directions-btn"
              >
                <Button className="w-full rounded-full gap-2 bg-primary hover:bg-primary/90">
                  <Navigation className="w-5 h-5" />
                  Obtenir l'itinéraire
                </Button>
              </a>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}

export { ContactPage };
